import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, LogOut, Search, MessageCircle, Loader2, Package, DollarSign, Clock, CalendarDays, RefreshCw, Download, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ServicesManager from "@/components/ServicesManager";
import ReportPanel from "@/components/ReportPanel";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatBRL, whatsappLink } from "@/lib/api";
import { CATEGORIES } from "@/data/content";

const STATUS_META = {
  pendente: { label: "Pendente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  em_processamento: { label: "Em Processamento", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  concluido: { label: "Concluído", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cancelado: { label: "Cancelado", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin/login");
  }, [authLoading, user, navigate]);

  const buildParams = useCallback(() => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (categoryFilter !== "all") params.category = categoryFilter;
    if (search) params.search = search;
    if (start) params.start = start;
    if (end) params.end = end;
    return params;
  }, [statusFilter, categoryFilter, search, start, end]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([api.get("/orders", { params: buildParams() }), api.get("/admin/stats")]);
      setOrders(o.data);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [buildParams, navigate]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export", { params: buildParams(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pedidos_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao exportar");
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success("Status atualizado");
      load();
    } catch {
      toast.error("Erro ao atualizar status");
    }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/orders/${id}/mark-paid`);
      toast.success("Pix marcado como recebido");
      load();
    } catch {
      toast.error("Erro ao marcar Pix recebido");
    }
  };

  if (authLoading || !user) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#0F294A]" /></div>;
  }

  const STAT_CARDS = stats
    ? [
        { icon: Package, label: "Total de Pedidos", value: stats.total_orders, color: "text-[#0F294A] bg-[#0F294A]/10" },
        { icon: DollarSign, label: "Receita Confirmada", value: formatBRL(stats.total_revenue), color: "text-emerald-600 bg-emerald-100" },
        { icon: CalendarDays, label: "Pedidos Hoje", value: stats.today_orders, color: "text-blue-600 bg-blue-100" },
        { icon: Clock, label: "Pendentes", value: stats.pending_orders, color: "text-amber-600 bg-amber-100" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-[#0F294A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/10"><ShieldCheck className="h-5 w-5 text-emerald-400" /></span>
            <span className="font-heading font-bold">Painel · Central MEI</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-300 hidden sm:block">{user.email}</span>
            <Button data-testid="admin-logout-button" onClick={() => { logout(); navigate("/admin/login"); }} variant="ghost" size="sm" className="text-slate-200 hover:text-white hover:bg-white/10 gap-1.5">
              <LogOut className="h-4 w-4" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="pedidos">
          <TabsList className="mb-6">
            <TabsTrigger value="pedidos" data-testid="tab-pedidos">Pedidos</TabsTrigger>
            <TabsTrigger value="servicos" data-testid="tab-servicos">Serviços</TabsTrigger>
            <TabsTrigger value="relatorios" data-testid="tab-relatorios">Relatórios</TabsTrigger>
          </TabsList>
          <TabsContent value="servicos"><ServicesManager /></TabsContent>
          <TabsContent value="relatorios"><ReportPanel /></TabsContent>
          <TabsContent value="pedidos">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading font-bold text-2xl text-slate-900">Pedidos recebidos</h1>
          <Button data-testid="admin-refresh" onClick={load} variant="outline" size="sm" className="gap-1.5"><RefreshCw className="h-4 w-4" /> Atualizar</Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {STAT_CARDS.map((c) => (
            <div key={c.label} data-testid={`stat-${c.label}`} className="bg-white rounded-2xl border border-slate-200 p-5">
              <span className={`grid place-items-center h-10 w-10 rounded-xl mb-3 ${c.color}`}><c.icon className="h-5 w-5" /></span>
              <p className="font-heading font-extrabold text-2xl text-slate-900">{c.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, CPF ou protocolo..." className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-filter-status" className="sm:w-48"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="admin-filter-category" className="sm:w-40"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.short}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" data-testid="orders-start" value={start} onChange={(e) => setStart(e.target.value)} className="sm:w-40" title="Data inicial" />
            <Input type="date" data-testid="orders-end" value={end} onChange={(e) => setEnd(e.target.value)} className="sm:w-40" title="Data final" />
            <Button data-testid="orders-export" onClick={exportCsv} variant="outline" className="gap-1.5 shrink-0"><Download className="h-4 w-4" /> CSV</Button>
          </div>

          {loading ? (
            <div className="py-20 grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>
          ) : orders.length === 0 ? (
            <div className="py-20 text-center text-slate-400" data-testid="admin-empty"><Package className="h-10 w-10 mx-auto mb-3 opacity-50" />Nenhum pedido encontrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Protocolo</th>
                    <th className="text-left font-semibold px-4 py-3">Cliente</th>
                    <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Serviço</th>
                    <th className="text-left font-semibold px-4 py-3">Valor</th>
                    <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">Pgto</th>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                    <th className="text-right font-semibold px-4 py-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.protocol}`} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs font-bold text-[#0F294A]">{o.protocol}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{o.name}</p>
                        <p className="text-xs text-slate-400">{o.cpf}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{o.service_name}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{formatBRL(o.amount)}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {o.payment_status === "paid" ? (
                          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50">Pago</Badge>
                        ) : o.payment_method === "pix" ? (
                          <Button data-testid={`order-mark-paid-${o.protocol}`} size="sm" variant="outline" onClick={() => markPaid(o.id)} className="h-7 gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Marcar Pix recebido
                          </Button>
                        ) : (
                          <Badge variant="outline" className="border-slate-200 text-slate-500">Pendente</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                          <SelectTrigger data-testid={`order-status-${o.protocol}`} className={`h-8 w-auto gap-1.5 border text-xs font-semibold ${STATUS_META[o.status].cls}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <a href={whatsappLink(`Olá ${o.name}! Sobre seu pedido ${o.protocol} (${o.service_name}) na Central de Serviços do MEI...`)} target="_blank" rel="noreferrer">
                          <Button data-testid={`order-whatsapp-${o.protocol}`} size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1.5"><MessageCircle className="h-4 w-4" /><span className="hidden sm:inline">WhatsApp</span></Button>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
